import { Injectable } from '@angular/core';
import { ElevenLabsVoiceService } from './elevenlabs-voice.service';

export type RealtimeTranscriptHandlers = {
    partial: (text: string) => void;
    committed: (text: string) => void;
    error: () => void;
};

@Injectable({ providedIn: 'root' })
export class ElevenLabsRealtimeSttService {
    private socket?: WebSocket;
    private stream?: MediaStream;
    private context?: AudioContext;
    private processor?: ScriptProcessorNode;

    constructor(private voice: ElevenLabsVoiceService) {}

    async start(handlers: RealtimeTranscriptHandlers): Promise<void> {
        this.stop();
        const token = await this.voice.createScribeToken();
        if (!token) throw new Error('Missing Scribe token');
        this.stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
        });
        this.context = new AudioContext();
        const source = this.context.createMediaStreamSource(this.stream);
        this.processor = this.context.createScriptProcessor(4096, 1, 1);
        const silence = this.context.createGain();
        silence.gain.value = 0;
        source.connect(this.processor);
        this.processor.connect(silence);
        silence.connect(this.context.destination);

        const url = new URL('wss://api.elevenlabs.io/v1/speech-to-text/realtime');
        url.searchParams.set('token', token);
        url.searchParams.set('model_id', 'scribe_v2_realtime');
        url.searchParams.set('audio_format', 'pcm_16000');
        url.searchParams.set('language_code', 'de');
        url.searchParams.set('commit_strategy', 'vad');
        url.searchParams.set('vad_silence_threshold_secs', '1');
        url.searchParams.set('min_speech_duration_ms', '180');
        url.searchParams.set('min_silence_duration_ms', '180');
        url.searchParams.set('filter_background_audio', 'true');
        this.socket = new WebSocket(url);
        this.socket.onmessage = (event) => {
            const data = JSON.parse(event.data || '{}');
            if (data.message_type === 'partial_transcript') handlers.partial(String(data.text || ''));
            if (data.message_type === 'committed_transcript') handlers.committed(String(data.text || ''));
            if (/error|rate_limited|quota_exceeded/i.test(String(data.message_type || ''))) handlers.error();
        };
        this.socket.onerror = () => handlers.error();
        this.processor.onaudioprocess = (event) => {
            if (this.socket?.readyState !== WebSocket.OPEN) return;
            const audio = event.inputBuffer.getChannelData(0);
            this.socket.send(JSON.stringify({
                message_type: 'input_audio_chunk',
                audio_base_64: this.toBase64(this.resampleToPcm16(audio, this.context?.sampleRate || 48000)),
            }));
        };
    }

    stop(): void {
        this.processor?.disconnect();
        this.processor = undefined;
        this.stream?.getTracks().forEach(track => track.stop());
        this.stream = undefined;
        this.context?.close();
        this.context = undefined;
        this.socket?.close();
        this.socket = undefined;
    }

    private resampleToPcm16(input: Float32Array, inputRate: number): Uint8Array {
        const ratio = inputRate / 16000;
        const output = new Int16Array(Math.round(input.length / ratio));
        for (let index = 0; index < output.length; index++) {
            const sourceIndex = Math.min(Math.round(index * ratio), input.length - 1);
            const sample = Math.max(-1, Math.min(1, input[sourceIndex]));
            output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        }
        return new Uint8Array(output.buffer);
    }

    private toBase64(bytes: Uint8Array): string {
        let binary = '';
        for (let index = 0; index < bytes.length; index++) binary += String.fromCharCode(bytes[index]);
        return btoa(binary);
    }
}
